import { useEffect, useRef, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { ws } from "../api/ws";
import type { Envelope, FleetDelta } from "@paperclip-cloud/shared";
import { useUiStore } from "../stores/ui";
import { fleetKey, type FleetCache } from "../hooks/useFleet";

/**
 * Throttled live region for fleet status changes (one announcement / 3s).
 * Mounted once at app shell level. Separate from the CEO stream live region.
 */
export function FleetAnnouncer() {
  const [text, setText] = useState("");
  const queueRef = useRef<string[]>([]);
  const lastEmitRef = useRef(0);
  const timerRef = useRef<number | null>(null);
  const activeCompanyId = useUiStore((s) => s.activeCompanyId);
  const qc = useQueryClient();

  useEffect(() => {
    const off = ws.subscribe((env: Envelope) => {
      if (env.t !== "fleet.delta") return;
      const delta = env.body as FleetDelta;
      if (delta.company_id !== activeCompanyId) return;
      const cache = qc.getQueryData<FleetCache>(fleetKey(delta.company_id));
      const nameOf = (id: string) =>
        cache?.devices.find((d) => d.id === id)?.name ?? "device";
      for (const c of delta.changes) {
        if (c.kind === "device.online") queueRef.current.push(`${nameOf(c.device_id)} connected`);
        if (c.kind === "device.offline") queueRef.current.push(`${nameOf(c.device_id)} disconnected`);
        if (c.kind === "device.added") queueRef.current.push(`${c.device.name} paired`);
      }
      drain();
    });
    return off;
  }, [activeCompanyId, qc]);

  function drain() {
    if (timerRef.current != null) return;
    const since = Date.now() - lastEmitRef.current;
    const wait = Math.max(0, 3000 - since);
    timerRef.current = window.setTimeout(() => {
      timerRef.current = null;
      const next = queueRef.current.shift();
      if (!next) return;
      lastEmitRef.current = Date.now();
      setText(next);
      if (queueRef.current.length > 0) drain();
    }, wait);
  }

  return (
    <div role="status" aria-live="polite" className="sr-only">
      {text}
    </div>
  );
}
